// /skills: derived levels per person per task (never stored), and the
// promotions waiting for a supervisor's "Looks right".
import { useIonToast } from "@ionic/react";
import { useMemo } from "react";
import type { SkillRow } from "../lib/types";
import { announce, can, useApi, useMe } from "../api/live";
import { post } from "../api/client";
import { paths } from "../api/paths";
import { dayMonth } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { Page } from "../shell/Page";
import { ErrorNote, Loading } from "../shell/ui";

const LEVEL = ["Never", "Learner", "Able", "Strong"];

export function SkillsPage() {
  const { me } = useMe();
  const [present] = useIonToast();
  const skills = useApi<SkillRow[]>(paths.skills(), ["skill", "session"]);
  const { tasks } = useTasks();
  const rows = skills.data ?? [];
  const people = useMemo(() => [...new Set(rows.map((s) => s.person))], [rows]);
  const codes = tasks.map((t) => t.code).filter((c) => rows.some((s) => s.task === c));
  const at = (p: string, t: string) => rows.find((s) => s.person === p && s.task === t);
  const reviews = rows.filter((s) => s.review);
  const ack = async (s: SkillRow) => {
    try {
      await post(paths.acknowledgeSkill(), { person: s.person, task: s.task, level: s.review!.level, at: s.review!.at });
      present({ message: `${s.person} on ${s.task}: confirmed`, duration: 2000 });
      announce(["skill"]);
    } catch (e) { present({ message: (e as Error).message, duration: 3000, color: "danger" }); }
  };
  return (
    <Page title="Skills">
      {!skills.data ? (skills.error ? <ErrorNote error={skills.error} retry={skills.reload} /> : <Loading />) : (
        <div className="stack">
          {reviews.length ? (
            <section className="card">
              <div className="card__head"><h2>Promotions waiting</h2><span className="small muted">Levels change only when a supervisor says so</span></div>
              {reviews.map((s) => (
                <div key={s.person + s.task} className="settings-row">
                  <span className="settings-row__k"><b>{s.person}</b> to {LEVEL[s.review!.level]} on {s.task}<small>{s.review!.why} · {dayMonth(s.review!.at)}</small></span>
                  {can(me, "supervisor") ? <button type="button" className="btn btn--sm btn--primary" onClick={() => ack(s)}>Looks right</button> : null}
                </div>
              ))}
            </section>
          ) : null}
          <section className="card">
            <div className="legend">
              {LEVEL.map((l, i) => <span key={l} className={`lv lv--${i}`}>{l}</span>)}
              <span>★ trains others · the small number is weeks since last done. Levels lapse at 12 and 26 weeks.</span>
            </div>
            <div className="scroll-x">
              <table className="skills">
                <thead><tr><th>Person</th>{codes.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{people.map((p) => (
                  <tr key={p}><td>{p}</td>{codes.map((c) => {
                    const s = at(p, c);
                    const lv = s?.level ?? 0;
                    return (
                      <td key={c}><span className={`lv lv--${lv}${s?.review ? " is-review" : ""}`} title={`${p}, ${c}: ${LEVEL[lv]}${s?.expert ? ", trains others" : ""}${s?.nextPct && s.nextPct < 100 ? `, planned at ${s.nextPct}%` : ""}`}>
                        {lv ? `${lv}${s?.expert ? "★" : ""}` : "·"}{s?.weeksSince != null ? <small>{s.weeksSince}w</small> : null}
                      </span></td>
                    );
                  })}</tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </Page>
  );
}
